import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  Grid,
  Divider,
  IconButton,
  Link,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TimerIcon from '@mui/icons-material/Timer';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import InfoIcon from '@mui/icons-material/Info';
import CategoryIcon from '@mui/icons-material/Category';
import SortIcon from '@mui/icons-material/Sort';
import ShortTextIcon from '@mui/icons-material/ShortText';
import UpdateIcon from '@mui/icons-material/Update';
import TagIcon from '@mui/icons-material/Tag';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Dashboard, Category } from '../services/DashboardsService';
import DashboardForm from './DashboardForm';

interface DashboardDetailsDialogProps {
  dashboard: Dashboard | null;
  categories: Category[];
  dashboardsByCategory: { [categoryId: string]: Dashboard[] };
  open: boolean;
  onClose: () => void;
  onUpdate: (dashboard: Dashboard) => Promise<boolean>;
}

/**
 * Component to display detailed information about a dashboard in a dialog
 */
const DashboardDetailsDialog: React.FC<DashboardDetailsDialogProps> = ({
  dashboard,
  categories,
  dashboardsByCategory,
  open,
  onClose,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(dashboard);

  // Update the internal dashboard state when the prop changes
  useEffect(() => {
    if (dashboard) {
      setCurrentDashboard(dashboard);
    }
  }, [dashboard]);

  if (!currentDashboard) {
    return null;
  }

  // Format the date to a more readable format
  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Determine dashboard status
  const getDashboardStatus = (): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
    if (currentDashboard.deactivated) {
      return { label: 'Deactivated', color: 'error' };
    } else if (currentDashboard.placeholder) {
      return { label: 'Placeholder', color: 'warning' };
    } else {
      return { label: 'Active', color: 'success' };
    }
  };

  const status = getDashboardStatus();

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
  };

  const handleSaveDashboard = async (updatedDashboard: Dashboard) => {
    const success = await onUpdate(updatedDashboard);
    if (success) {
      setCurrentDashboard(updatedDashboard);
      setIsEditing(false);
    }
    return success;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="dashboard-details-dialog-title"
    >
      <DialogTitle id="dashboard-details-dialog-title" sx={{ pr: 8 }}>
        {isEditing ? 'Edit Dashboard' : 'Dashboard Details'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        
        {!isEditing && (
          <IconButton
            aria-label="edit"
            onClick={handleStartEditing}
            sx={{
              position: 'absolute',
              right: 48,
              top: 8,
            }}
          >
            <EditIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent dividers>
        {isEditing ? (
          <DashboardForm 
            initialValues={currentDashboard}
            categories={categories}
            dashboardsByCategory={dashboardsByCategory}
            onSave={handleSaveDashboard}
            onCancel={handleCancelEditing}
          />
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                {currentDashboard.name}
                <Chip
                  label={status.label}
                  color={status.color}
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                />
              </Typography>
              
              {currentDashboard.subtitle && (
                <Typography variant="subtitle1" color="text.secondary" paragraph>
                  {currentDashboard.subtitle}
                </Typography>
              )}
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {currentDashboard.category?.name || 'Uncategorized'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <TagIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                </Box>
                <Typography variant="body1">
                  <Chip 
                    size="small"
                    label={currentDashboard.type} 
                    color={
                      currentDashboard.type === 'Superset' ? 'primary' : 
                      'default'
                    } 
                  />
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <ShortTextIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Short Name
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {currentDashboard.shortName || 'N/A'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <UpdateIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {formatDate(currentDashboard.updateDate)}
                </Typography>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md:4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  {currentDashboard.internalOnly ? (
                    <VisibilityOffIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  ) : (
                    <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Internal Only
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {currentDashboard.internalOnly ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md:4 }}>
                <Typography variant="body2" color="text.secondary">
                  Access Only
                </Typography>
                <Typography variant="body1">
                  {currentDashboard.accessOnly ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md:4 }}>
                <Typography variant="body2" color="text.secondary">
                  Listen Reporting
                </Typography>
                <Typography variant="body1">
                  {currentDashboard.listenReporting ? 'Yes' : 'No'}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md:4 }}>
                <Typography variant="body2" color="text.secondary">
                  HXC Program
                </Typography>
                <Typography variant="body1">
                  {currentDashboard.hxcProgram ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md:4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <SortIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Order
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {currentDashboard.orderBy}
                </Typography>
              </Grid>

              {/* New fields section */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                  Extended Information
                </Typography>
              </Grid>

              {/* Icon field */}
              {currentDashboard.icon && (
                <Grid size={{ xs: 12, sm: 6, md:4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <InsertEmoticonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Icon
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {currentDashboard.icon}
                  </Typography>
                </Grid>
              )}

              {/* Thumbnail field */}
              {currentDashboard.thumbnail && (
                <Grid size={{ xs: 12, sm: 6, md:4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <ImageIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Thumbnail
                    </Typography>
                    <Tooltip title={currentDashboard.thumbnail} arrow placement="top">
                      <InfoIcon 
                        fontSize="small" 
                        color="action" 
                        sx={{ ml: 0.5, fontSize: '1rem', cursor: 'pointer' }} 
                      />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {currentDashboard.thumbnail.startsWith('http') ? (
                      <Link href={currentDashboard.thumbnail} target="_blank" rel="noopener noreferrer">
                        View Thumbnail
                      </Link>
                    ) : (
                      currentDashboard.thumbnail
                    )}
                  </Typography>
                </Grid>
              )}

              {/* Demo field */}
              {currentDashboard.demo && (
                <Grid size={{ xs: 12, sm: 6, md:4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <OndemandVideoIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Demo
                    </Typography>
                    <Tooltip title={currentDashboard.demo} arrow placement="top">
                      <InfoIcon 
                        fontSize="small" 
                        color="action" 
                        sx={{ ml: 0.5, fontSize: '1rem', cursor: 'pointer' }} 
                      />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {currentDashboard.demo.startsWith('http') ? (
                      <Link href={currentDashboard.demo} target="_blank" rel="noopener noreferrer">
                        View Demo
                      </Link>
                    ) : (
                      currentDashboard.demo
                    )}
                  </Typography>
                </Grid>
              )}

              {/* Downtime field */}
              {currentDashboard.downtime && (
                <Grid size={{ xs: 12, sm: 6, md:4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <TimerIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Scheduled Downtime
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {new Date(currentDashboard.downtime).toLocaleString()}
                  </Typography>
                </Grid>
              )}
              
              {currentDashboard.resource && (
                <Grid size={{ xs: 12, sm: 6, md:4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <LinkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Resource
                    </Typography>
                    <Tooltip title={currentDashboard.resource} arrow placement="top">
                      <InfoIcon 
                        fontSize="small" 
                        color="action" 
                        sx={{ ml: 0.5, fontSize: '1rem', cursor: 'pointer' }} 
                      />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {currentDashboard.resource.startsWith('http') ? (
                      <Link href={currentDashboard.resource} target="_blank" rel="noopener noreferrer">
                        View Resource
                      </Link>
                    ) : (
                      currentDashboard.resource
                    )}
                  </Typography>
                </Grid>
              )}

              {currentDashboard.embedId && (
                <Grid size={{ xs: 12, sm: 6, md:4 }} sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CodeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Embed Id
                    </Typography>
                  </Box>
                  <Tooltip title={currentDashboard.embedId} arrow placement="top">
                    <Typography variant="body1" noWrap>
                      {currentDashboard.embedId}
                    </Typography>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        {isEditing ? (
          <></>
        ) : (
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DashboardDetailsDialog;